/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { colors } from '../../../shared/theme/colors';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';

type PosVariousModalProps = {
  visible: boolean;
  name: string;
  amount: string;
  isEditing?: boolean;
  onClose: () => void;
  onChangeName: (value: string) => void;
  onChangeAmount: (value: string) => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;
};

export const PosVariousModal = ({
  visible,
  name,
  amount,
  isEditing = false,
  onClose,
  onChangeName,
  onChangeAmount,
  onSave,
  saving = false,
}: PosVariousModalProps) => {
  const [saveAttempted, setSaveAttempted] = useState(false);
  const trimmedAmount = amount.trim();
  const parsedAmount = trimmedAmount === '' ? null : parseDecimalInput(amount, { min: 0 });
  const amountInvalid = trimmedAmount.length === 0 || parsedAmount === null || parsedAmount <= 0;
  const showAmountError = saveAttempted && amountInvalid;

  useEffect(() => {
    if (!visible) {
      setSaveAttempted(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!amountInvalid) {
      setSaveAttempted(false);
    }
  }, [amountInvalid]);

  const amountHelperText = useMemo(() => {
    if (!showAmountError) return null;
    if (trimmedAmount.length === 0) return 'Ingresá un monto';
    if (parsedAmount === null) return 'Monto inválido';
    return 'El monto debe ser mayor a 0';
  }, [parsedAmount, showAmountError, trimmedAmount.length]);

  const handleSavePress = () => {
    if (saving) return;
    if (amountInvalid) {
      setSaveAttempted(true);
      return;
    }
    onSave();
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      onRequestClose={saving ? undefined : onClose}
      title={isEditing ? 'Editar venta rápida' : 'Venta rápida'}
      subtitle="Un monto suelto, sin producto del catálogo."
      closeAccessibilityLabel="Cerrar venta rápida"
      scrollable={false}
      footer={
        <View style={[styles.modalActions, modalExperience.footerActions]}>
          <TouchableOpacity
            style={[styles.secondaryButton, styles.modalButtonHalf]}
            onPress={onClose}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel="Cancelar venta rápida"
          >
            <Text style={styles.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.posPrimaryCta,
              styles.modalButtonHalf,
              saving && { opacity: 0.6 },
            ]}
            onPress={handleSavePress}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel={isEditing ? 'Guardar' : 'Agregar'}
            testID="pos-various-add"
          >
            {saving ? (
              <ActivityIndicator color={ticketColors.goldInk} />
            ) : (
              <Text style={styles.posPrimaryCtaText}>{isEditing ? 'Guardar' : 'Agregar'}</Text>
            )}
          </TouchableOpacity>
        </View>
      }
    >
      <View style={modalExperience.formPanel}>
        <ModalFormField label="Descripción">
          <TextInput
            value={name}
            onChangeText={onChangeName}
            placeholder="Ej: Servicio de entrega"
            placeholderTextColor={colors.textMuted}
            style={modalExperience.formInput}
            returnKeyType="next"
            editable={!saving}
            accessibilityLabel="Descripción de la venta rápida"
          />
        </ModalFormField>
        <ModalFormField label="Monto" required>
          <NumericTextInput
            kind="decimalMoney"
            value={amount}
            onChangeText={onChangeAmount}
            placeholder="0,00"
            placeholderTextColor={colors.textMuted}
            style={[
              modalExperience.formInput,
              showAmountError && { borderColor: colors.warning },
            ]}
            returnKeyType="done"
            editable={!saving}
          />
          {amountHelperText ? (
            <Text
              style={[
                modalExperience.formLabel,
                { fontWeight: '500' },
                showAmountError && { color: colors.warning },
              ]}
            >
              {amountHelperText}
            </Text>
          ) : null}
        </ModalFormField>
      </View>
    </ModalShell>
  );
};
